import { useCallback, useState } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import { getDarkModePreferred } from "@hopper-b2b/themes";
import {
  ActionButton,
  ButtonWrap,
  IconComponent,
  IconName,
  MobilePopoverCard,
} from "@hopper-b2b/ui";
import {
  useEnableDarkMode,
  useEnablePriceDropProtection,
} from "@hopper-b2b/utilities";
import clsx from "clsx";
import ClockIcon from "../../../assets/client/clock-simple-icon.svg?react";
import ClockIconWhite from "../../../assets/client/darkMode/clock-simple-icon.svg?react";
import InfoIconWhite from "../../../assets/client/darkMode/info-white.svg";
import MoneyIconWhite from "../../../assets/client/darkMode/money-note-white.svg?react";
import PriceDropProtectionIconWhite from "../../../assets/client/darkMode/price-drop-protection-icon.svg";
import TicketIconWhite from "../../../assets/client/darkMode/ticket-white.svg?react";
import InfoIcon from "../../../assets/client/info.svg";
import MoneyIcon from "../../../assets/client/money-note.svg?react";
import PdpHeaderImage from "../../../assets/client/pdp-header.svg";
import PriceDropProtectionIcon from "../../../assets/client/price-drop-protection-icon.svg";
import TicketIcon from "../../../assets/client/ticket.svg?react";
import styles from "./styles.module.scss";

interface FlightPriceDropProtectionBannerProps {
  isEligible: boolean;
  showMore?: boolean;
  className?: string;
  inReview?: boolean;
}

const FlightPriceDropProtectionBanner = ({
  isEligible,
  showMore = false,
  className,
  inReview = false,
}: FlightPriceDropProtectionBannerProps) => {
  const { t } = useI18nContext();

  const enabledPriceDropProtection = useEnablePriceDropProtection();

  const darkModeAllowed = useEnableDarkMode();
  const isDarkMode = darkModeAllowed && getDarkModePreferred();

  const [openModal, setOpenModal] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);

  const handleOpenModal = useCallback(() => {
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  const Clock = isDarkMode ? ClockIconWhite : ClockIcon;
  const Money = isDarkMode ? MoneyIconWhite : MoneyIcon;
  const Ticket = isDarkMode ? TicketIconWhite : TicketIcon;

  return isEligible && enabledPriceDropProtection ? (
    <>
      <ButtonWrap
        className={clsx(styles.pdpBannerContainer, className)}
        onClick={showMore ? handleOpenModal : null}
      >
        <div className="pdp-content">
          <div className="pdp-content-left">
            <img
              className="pdp-icon"
              src={
                isDarkMode
                  ? PriceDropProtectionIconWhite
                  : PriceDropProtectionIcon
              }
              alt=""
            />
            <p>
              {t(
                inReview
                  ? "priceDrop.title"
                  : "priceForecast.recommendedInfoLabel"
              )}
            </p>
          </div>
          <img
            className="pdp-info"
            src={isDarkMode ? InfoIconWhite : InfoIcon}
            alt=""
          />
          <p className="pdp-learn-more">{t("learnMore")}</p>
        </div>
      </ButtonWrap>

      <MobilePopoverCard
        open={openModal}
        onClose={handleCloseModal}
        paperClassName={styles.infoModalPaper}
        className={styles.infoModal}
        topRightButton={
          <IconComponent
            name={IconName.Close}
            onClick={handleCloseModal}
            className="close-button"
          />
        }
      >
        <div className="image-container">
          <img
            src={PdpHeaderImage}
            alt="person in car with price drop protection on phone"
            className="pdp-header-image"
          />
        </div>
        <div className="content">
          <h1 className="info-modal-title">
            {t("priceDropProtection.modalTitle")}
          </h1>
          <p className="info-modal-subtitle">
            {t("priceDropProtection.modalSubtitle")}
          </p>

          <div className="value-prop-row">
            <Ticket className="value-prop-icon" />
            <p className="value-prop-text">
              {t("priceDropProtection.bookFollowingRecommendation")}
            </p>
          </div>

          <div className="value-prop-row">
            <Clock className="value-prop-icon" />
            <p className="value-prop-text">
              {t("priceDropProtection.monitorPriceTime")}
            </p>
          </div>

          <div className="value-prop-row">
            <Money className="value-prop-icon" />
            <p className="value-prop-text">
              {t("priceDropProtection.monitorPriceDrop")}
            </p>
          </div>

          {/* Terms link button */}
          <ButtonWrap
            className="terms-wrapper"
            onClick={() => setOpenTerms(true)}
          >
            <p>{t("ftu.termsAndConditions")}</p>
          </ButtonWrap>
          <ActionButton onClick={handleCloseModal} message={t("gotIt")} />
        </div>
      </MobilePopoverCard>
      <MobilePopoverCard
        open={openTerms}
        onClose={() => setOpenTerms(false)}
        paperClassName={styles.termsAndConditionsModalPaper}
        className={styles.termsAndConditionsModal}
        topLeftButton={
          <IconComponent
            name={IconName.Close}
            onClick={() => setOpenTerms(false)}
            className="close-button"
          />
        }
      >
        <h1 className="modal-title">
          {t("priceDropProtection.termsModalTitle")}
        </h1>
        <p className="modal-subtitle">
          {t("priceDropProtection.termsAndConditions")}
        </p>
        <p className="modal-terms-copy">
          {t("priceDropProtection.termsAndConditionsCopy")}
        </p>
      </MobilePopoverCard>
    </>
  ) : null;
};

export default FlightPriceDropProtectionBanner;
